import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";

const TERMS_RECHECK_INTERVAL_MS = 60_000;

export function useOrganizationTerms({
  organizationId,
  enabled = true,
  recheckPeriodically = false,
}: {
  organizationId: string;
  enabled?: boolean;
  recheckPeriodically?: boolean;
}) {
  const trpc = useTRPC();
  const queryEnabled = Boolean(organizationId) && enabled;
  const query = useQuery(
    trpc.organization.getUserOrganizations.queryOptions(undefined, {
      enabled: queryEnabled,
      // The route guard loads organizations before mounting either terms view.
      // Reuse that result so startup does not perform a second blocking read.
      refetchOnMount: false,
      refetchOnWindowFocus: "always",
      refetchInterval: recheckPeriodically ? TERMS_RECHECK_INTERVAL_MS : false,
      refetchIntervalInBackground: false,
      select: (organizations) =>
        organizations.find((organization) => organization.id === organizationId)
          ?.termsAcceptance,
    }),
  );

  // A successful refresh can remove access to the selected organization. Treat
  // an absent status as unavailable rather than allowing content or waiting forever.
  const missingOrganization = queryEnabled && query.isSuccess && !query.data;

  return {
    data: query.data,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError || missingOrganization,
    isSuccess: query.isSuccess && !missingOrganization,
    refetch: query.refetch,
  };
}
